import qs from 'qs';
import { AxiosResponse } from 'axios';
import { ContractView, Invoice, InvoiceView, Pagination } from '~/types';
import {
  ContractHome,
  Contratos90Dias,
  Home,
  ValoresFaturados,
} from '~/types/home';
import api from '~/utils/api';

import {
  addFormattedPropertiesToContract,
  addFormattedPropertiesToInvoice,
} from '..';
import { UsersContactResponse } from '~/types/userscontact';
import { formatCurrency } from '~/utils/formatCurrency';
import { InsuranceList } from '~/types/insurance';

export async function getHome(
  invoiceStatus: string,
  query?: any,
): Promise<Home> {
  const { data } = await api.get<Home>('/home', {
    params: {
      ...(invoiceStatus
        ? { st_pagamento: invoiceStatus, ...query }
        : { ...query }), // query chega aqui com um objeto aninhado eu preciso remover as propriedades que eu quero e converter para um array antes de jogar para a url
    },
    paramsSerializer: params => {
      return qs.stringify(params, { arrayFormat: 'repeat' });
    },
  });

  const contratos90dias =
    data?.contratos90dias?.map(
      contract => addFormattedPropertiesToContract(contract) as ContractHome,
    ) || [];

  const contratosEncerrados = data?.contratos_encerrados?.map(
    contract => addFormattedPropertiesToContract(contract) as ContractHome,
  );

  const valoresFaturados = {
    months: data.valoresFaturados?.months || [],
    total_faturado:
      data?.valoresFaturados?.total_faturado?.map(item =>
        formatCurrency(Number(item)),
      ) || [],
    total_contratos_iniciais:
      data?.valoresFaturados?.total_contratos_iniciais?.map(item =>
        formatCurrency(Number(item)),
      ) || [],
    total_contratos_estendidos:
      data?.valoresFaturados?.total_contratos_estendidos?.map(item =>
        formatCurrency(Number(item)),
      ) || [],
    total_pago:
      data?.valoresFaturados?.total_pago?.map(item =>
        formatCurrency(Number(item)),
      ) || [],
    total_em_aberto:
      data?.valoresFaturados?.total_em_aberto?.map(item =>
        formatCurrency(Number(item)),
      ) || [],
  };

  const formattedHome = {
    ...data,
    contratos90dias,
    contratosEncerrados,
    valoresFaturados,
  };

  return formattedHome;
}

export async function getPieChart(
  invoiceStatus: string,
  query?: any,
): Promise<any[]> {
  const { data } = await api.get<Home>('/home/pie_chart', {
    params: {
      ...(invoiceStatus
        ? { st_pagamento: invoiceStatus, ...query }
        : { ...query }),
    },
    paramsSerializer: params => {
      return qs.stringify(params, { arrayFormat: 'repeat' });
    },
  });

  return data.pieChart;
}

export async function getContratosAndamento(
  invoiceStatus: string,
  query?: any,
): Promise<number> {
  const { data } = await api.get<Home>('/home/contratos_em_andamento', {
    params: {
      ...(invoiceStatus
        ? { st_pagamento: invoiceStatus, ...query }
        : { ...query }),
    },
    paramsSerializer: params => {
      return qs.stringify(params, { arrayFormat: 'repeat' });
    },
  });

  return data.contratos;
}

export async function getClientes(
  invoiceStatus: string,
  query?: any,
): Promise<number> {
  const { data } = await api.get<Home>('/home/clientes', {
    params: {
      ...(invoiceStatus
        ? { st_pagamento: invoiceStatus, ...query }
        : { ...query }),
    },
    paramsSerializer: params => {
      return qs.stringify(params, { arrayFormat: 'repeat' });
    },
  });

  return data.clientes;
}

export async function getFaturasEmAtraso(
  invoiceStatus: string,
  query?: any,
): Promise<number> {
  const { data } = await api.get<Home>('/home/faturas_em_atraso', {
    params: {
      ...(invoiceStatus
        ? { st_pagamento: invoiceStatus, ...query }
        : { ...query }),
    },
    paramsSerializer: params => {
      return qs.stringify(params, { arrayFormat: 'repeat' });
    },
  });

  return data.faturasEmAtraso;
}

export async function getContratos90Dias(
  invoiceStatus: string,
  query?: any,
): Promise<Contratos90Dias> {
  const { data } = await api.get<Contratos90Dias>(
    '/home/contratos_noventa_dias',
    {
      params: {
        ...(invoiceStatus
          ? { st_pagamento: invoiceStatus, ...query }
          : { ...query }),
      },
      paramsSerializer: params => {
        return qs.stringify(params, { arrayFormat: 'repeat' });
      },
    },
  );

  const contratos90dias =
    data?.contratos90dias?.map(
      contract => addFormattedPropertiesToContract(contract) as ContractHome,
    ) || [];

  return { ...data, contratos90dias };
}

export async function getContratosAguardandoReajuste(
  invoiceStatus: string,
  query?: any,
): Promise<number> {
  const { data } = await api.get<Home>('/home/contratos_aguardando_reajuste', {
    params: {
      ...(invoiceStatus
        ? { st_pagamento: invoiceStatus, ...query }
        : { ...query }),
    },
    paramsSerializer: params => {
      return qs.stringify(params, { arrayFormat: 'repeat' });
    },
  });

  return data.contratosAguardandoReajuste;
}

export async function getEquipamentosContratados(
  invoiceStatus: string,
  query?: any,
): Promise<number> {
  const { data } = await api.get<Home>('/home/equipamentos_contratados', {
    params: {
      ...(invoiceStatus
        ? { st_pagamento: invoiceStatus, ...query }
        : { ...query }),
    },
    paramsSerializer: params => {
      return qs.stringify(params, { arrayFormat: 'repeat' });
    },
  });

  return data.equipamentosContratados;
}

export async function getValoresFaturados(
  invoiceStatus: string,
  query?: any,
): Promise<ValoresFaturados> {
  const { data } = await api.get<Home>('/home/valores_faturados', {
    params: {
      ...(invoiceStatus
        ? { st_pagamento: invoiceStatus, ...query }
        : { ...query }),
    },
    paramsSerializer: params => {
      return qs.stringify(params, { arrayFormat: 'repeat' });
    },
  });

  const valoresFaturados = {
    months: data.valoresFaturados?.months || [],
    total_faturado:
      data?.valoresFaturados?.total_faturado?.map(item =>
        formatCurrency(Number(item)),
      ) || [],
    total_contratos_iniciais:
      data?.valoresFaturados?.total_contratos_iniciais?.map(item =>
        formatCurrency(Number(item)),
      ) || [],
    total_contratos_estendidos:
      data?.valoresFaturados?.total_contratos_estendidos?.map(item =>
        formatCurrency(Number(item)),
      ) || [],
    total_pago:
      data?.valoresFaturados?.total_pago?.map(item =>
        formatCurrency(Number(item)),
      ) || [],
    total_em_aberto:
      data?.valoresFaturados?.total_em_aberto?.map(item =>
        formatCurrency(Number(item)),
      ) || [],
  };

  return valoresFaturados;
}

export async function getHomeInvoices(
  page: number,
  query?: any,
): Promise<Pagination<InvoiceView>> {
  const { data } = await api.get<Pagination<Invoice>>('/home/faturas', {
    params: {
      page,
      size: 50,
      ...query,
    },
    paramsSerializer: params => {
      return qs.stringify(params, { arrayFormat: 'repeat' });
    },
  });

  const invoices = data.results.map(invoice =>
    addFormattedPropertiesToInvoice(invoice),
  );

  return {
    ...data,
    results: invoices,
  };
}

export async function getApolicesAVencer(
  page: number,
  query?: any,
): Promise<Pagination<InsuranceList>> {
  const { data } = await api.get<Pagination<InsuranceList>>(
    '/home/apolices_a_vencer',
    {
      params: {
        page,
        size: 50,
        ...query,
      },
      paramsSerializer: params => {
        return qs.stringify(params, { arrayFormat: 'repeat' });
      },
    },
  );

  const formattedData = data.results.map(insurance => ({
    ...insurance,
    formattedStatus:
      insurance.dias_vencimento === 0
        ? 'VENCE HOJE'
        : `VENCE EM ${insurance.dias_vencimento} ${
            insurance.dias_vencimento > 1 ? 'DIAS' : 'DIA'
          }`,
    statusColor: 'purple',
  }));

  return { ...data, results: formattedData };
}

export async function getHomeContacts(): Promise<UsersContactResponse> {
  const { data } = await api.get<UsersContactResponse>(
    '/home/contatos-usuarios',
  );

  return data;
}

export async function getHomeContractsXlsx(
  query?: any,
): Promise<AxiosResponse<any>> {
  const url = `/home/contratos-xlsx`;
  return api.get(url, {
    params: {
      ...query,
    },
    paramsSerializer: params => {
      return qs.stringify(params, { arrayFormat: 'repeat' });
    },
    headers: {
      'Content-Type': 'application/xlsx',
    },
    responseType: 'blob' as 'json',
  });
}
